import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import DashboardShell from '@/components/DashboardShell';
import api, { getImageUrl, PLACEHOLDER_IMAGE } from '@/api/client';
import AdminTable from '@/components/AdminTable';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  ArrowLeft, UserCog, CheckCircle, XCircle, Mail, MapPin, Globe, Phone,
  Image as ImageIcon, MessageSquare, CreditCard, Star, ExternalLink, Link2,
  UserX, UserCheck,
} from 'lucide-react';
import { toast } from 'sonner';

const ROLE_BADGES = {
  admin:     { label: 'Admin',     style: { backgroundColor: '#EDE9FE', color: '#5B21B6' } },
  member:    { label: 'Member',    style: { backgroundColor: '#D1FAE5', color: '#065F46' } },
  applicant: { label: 'Applicant', style: { backgroundColor: '#FEF3C7', color: '#92400E' } },
};
const STATUS_BADGE = {
  pending:  { label: 'Pending',  style: { backgroundColor: '#FEF3C7', color: '#92400E' } },
  approved: { label: 'Approved', style: { backgroundColor: '#D1FAE5', color: '#065F46' } },
  rejected: { label: 'Rejected', style: { backgroundColor: '#FEE2E2', color: '#991B1B' } },
};
const LISTING_BADGE = {
  active: { style: { backgroundColor: '#D1FAE5', color: '#065F46' } },
  draft:  { style: { backgroundColor: '#F3F4F6', color: '#374151' } },
  sold:   { style: { backgroundColor: '#EDE9FE', color: '#5B21B6' } },
};

const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
const formatPrice = (p) => (!p && p !== 0) ? '—' : new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP', maximumFractionDigits: 0 }).format(p);

function StatCard({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl p-4 flex items-center gap-3" style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border-clr)', boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
      <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(28,28,28,0.12)' }}>
        <Icon size={18} style={{ color: 'var(--gold)' }} />
      </div>
      <div>
        <p className="text-xl font-semibold" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>{value}</p>
        <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{label}</p>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value, isLink }) {
  return (
    <div className="flex items-start gap-3 py-2.5" style={{ borderBottom: '1px solid var(--border-clr)' }}>
      <Icon size={15} className="mt-0.5 flex-shrink-0" style={{ color: 'var(--text-muted)' }} />
      <div className="min-w-0">
        <p className="text-xs uppercase tracking-wide mb-0.5" style={{ color: 'var(--text-muted)', letterSpacing: '0.05em' }}>{label}</p>
        {isLink && value && value !== '—' ? (
          <a href={value.startsWith('http') ? value : `https://${value}`} target="_blank" rel="noreferrer" className="text-sm break-all hover:underline" style={{ color: 'var(--gold)' }}>{value}</a>
        ) : (
          <p className="text-sm break-words" style={{ color: 'var(--text)' }}>{value || '—'}</p>
        )}
      </div>
    </div>
  );
}

export default function MemberDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleDialog, setRoleDialog] = useState(false);
  const [newRole, setNewRole] = useState('');
  const [reviewDialog, setReviewDialog] = useState(null); // 'approved' | 'rejected'
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [suspending, setSuspending] = useState(false);

  const fetchProfile = useCallback(() => {
    setLoading(true);
    api.get(`/admin/members/${id}/profile`)
      .then(r => setData(r.data))
      .catch(() => toast.error('Failed to load member profile'))
      .finally(() => setLoading(false));
  }, [id]);

  useEffect(() => { fetchProfile(); }, [fetchProfile]);

  const user = data?.user;

  const submitRole = async () => {
    setSubmitting(true);
    try {
      await api.put(`/admin/members/${id}/role`, { role: newRole });
      toast.success('Role updated');
      setRoleDialog(false);
      fetchProfile();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update role');
    } finally { setSubmitting(false); }
  };

  const toggleSuspend = async () => {
    setSuspending(true);
    try {
      const r = await api.put(`/admin/members/${id}/suspend`);
      toast.success(r.data.suspended ? 'Member suspended — login and listings blocked' : 'Member unsuspended — access restored');
      fetchProfile();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setSuspending(false); }
  };

  const submitReview = async () => {    setSubmitting(true);
    try {
      await api.post(`/admin/applications/${id}/review`, { action: reviewDialog, admin_notes: notes });
      toast.success(`Application ${reviewDialog}`);
      setReviewDialog(null); setNotes('');
      fetchProfile();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return (
      <DashboardShell title="Member Profile">
        <div className="space-y-4">
          <Skeleton className="h-28 w-full rounded-2xl" />
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-20 rounded-2xl" />)}</div>
          <Skeleton className="h-64 w-full rounded-2xl" />
        </div>
      </DashboardShell>
    );
  }

  if (!user) {
    return (
      <DashboardShell title="Member Profile">
        <div className="py-20 text-center">
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Member not found.</p>
          <Button onClick={() => navigate('/admin/members')} variant="outline" className="mt-4" style={{ borderRadius: '10px' }}>Back to members</Button>
        </div>
      </DashboardShell>
    );
  }

  const roleBadge = ROLE_BADGES[user.role] || ROLE_BADGES.applicant;
  const statusBadge = STATUS_BADGE[user.membership_status];
  const stats = data.stats || {};

  return (
    <DashboardShell title="Member Profile">
      <div className="space-y-6 max-w-5xl">
        {/* Back */}
        <button onClick={() => navigate('/admin/members')} className="flex items-center gap-1.5 text-sm transition-colors hover:opacity-70" style={{ color: 'var(--text-muted)' }} data-testid="member-back-button">
          <ArrowLeft size={15} /> Back to members
        </button>

        {/* Header card */}
        <div className="rounded-2xl p-6 flex flex-col sm:flex-row sm:items-center gap-5" style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border-clr)', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' }} data-testid="member-profile-header">
          <div className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-semibold flex-shrink-0" style={{ backgroundColor: 'var(--gold)', fontFamily: 'var(--font-serif)' }}>
            {user.name?.charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-semibold" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }} data-testid="member-name">{user.name}</h2>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={roleBadge.style}>{roleBadge.label}</span>
              {statusBadge && <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium" style={statusBadge.style}>{statusBadge.label}</span>}
              {user.suspended && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium" style={{ backgroundColor: '#FEE2E2', color: '#991B1B' }} data-testid="suspended-badge">
                  <UserX size={11} /> Suspended
                </span>
              )}
            </div>
            <p className="text-sm mt-1" style={{ color: 'var(--text-muted)' }}>{user.email}</p>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-muted)' }}>Joined {formatDate(user.created_at)}</p>
          </div>
          {/* Admin actions */}
          <div className="flex flex-wrap gap-2">
            {user.membership_status === 'pending' && (
              <>
                <Button onClick={() => { setReviewDialog('approved'); setNotes(''); }} style={{ backgroundColor: 'var(--success)', color: 'white', borderRadius: '10px' }} data-testid="member-approve-button">
                  <CheckCircle size={14} className="mr-1.5" /> Approve
                </Button>
                <Button onClick={() => { setReviewDialog('rejected'); setNotes(''); }} style={{ backgroundColor: 'var(--danger)', color: 'white', borderRadius: '10px' }} data-testid="member-reject-button">
                  <XCircle size={14} className="mr-1.5" /> Reject
                </Button>
              </>
            )}
            <Button variant="outline" onClick={() => { setNewRole(user.role); setRoleDialog(true); }} style={{ borderRadius: '10px', borderColor: 'var(--border-strong)' }} data-testid="member-change-role-button">
              <UserCog size={14} className="mr-1.5" /> Change role
            </Button>
            {user.role !== 'admin' && (
              <Button onClick={toggleSuspend} disabled={suspending}
                style={{ backgroundColor: user.suspended ? 'var(--success)' : 'var(--danger)', color: 'white', borderRadius: '10px' }}
                data-testid="member-suspend-button">
                {user.suspended
                  ? <><UserCheck size={14} className="mr-1.5" /> Unsuspend</>
                  : <><UserX size={14} className="mr-1.5" /> Suspend</>}
              </Button>
            )}
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard icon={ImageIcon} label="Total listings" value={stats.total_listings ?? 0} />
          <StatCard icon={Star} label="Active listings" value={stats.active_listings ?? 0} />
          <StatCard icon={MessageSquare} label="Conversations" value={stats.total_conversations ?? 0} />
          <StatCard icon={CreditCard} label="Transactions" value={(data.transactions || []).length} />
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview">
          <TabsList style={{ backgroundColor: 'var(--bg-subtle)' }}>
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="listings" data-testid="tab-listings">Listings ({stats.total_listings ?? 0})</TabsTrigger>
            <TabsTrigger value="conversations" data-testid="tab-conversations">Conversations ({stats.total_conversations ?? 0})</TabsTrigger>
            <TabsTrigger value="transactions" data-testid="tab-transactions">Transactions</TabsTrigger>
          </TabsList>

          {/* Overview */}
          <TabsContent value="overview" className="mt-5">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="rounded-2xl p-5" style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border-clr)' }}>
                <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Contact & profile</h3>
                <InfoRow icon={Mail} label="Email" value={user.email} />
                <InfoRow icon={Phone} label="Phone" value={user.phone} />
                <InfoRow icon={MapPin} label="Location" value={user.location} />
                <InfoRow icon={Globe} label="Website" value={user.website} isLink />
                <InfoRow icon={Link2} label="Portfolio" value={user.portfolio_url} isLink />
              </div>
              <div className="rounded-2xl p-5" style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border-clr)' }}>
                <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Membership</h3>
                <InfoRow icon={UserCog} label="Profile type" value={user.profile_type || user.seller_type} />
                <InfoRow icon={CheckCircle} label="Membership status" value={user.membership_status || '—'} />
                <InfoRow icon={ImageIcon} label="Applied" value={formatDate(user.membership_applied_at)} />
                <InfoRow icon={CheckCircle} label="Reviewed" value={formatDate(user.membership_reviewed_at)} />
                <InfoRow icon={MessageSquare} label="Admin notes" value={user.membership_admin_notes} />
              </div>
            </div>
            {user.bio && (
              <div className="rounded-2xl p-5 mt-6" style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border-clr)' }}>
                <h3 className="text-sm font-semibold mb-2" style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Bio</h3>
                <p className="text-sm leading-relaxed" style={{ color: 'var(--text-muted)' }}>{user.bio}</p>
              </div>
            )}
          </TabsContent>

          {/* Listings */}
          <TabsContent value="listings" className="mt-5">
            <AdminTable
              data={data.listings || []}
              dataTestId="member-listings-table"
              emptyTitle="No listings uploaded"
              emptySubtitle="This member hasn’t created any listings yet."
              columns={[
                {
                  key: 'listing', label: 'Listing',
                  render: (l) => {
                    const img = l.images?.[0] ? getImageUrl(l.images[0]) : PLACEHOLDER_IMAGE;
                    return (
                      <div className="flex items-center gap-3">
                        <img src={img} alt={l.title} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" style={{ backgroundColor: 'var(--bg-subtle)' }} />
                        <div className="min-w-0">
                          <p className="font-medium truncate" style={{ color: 'var(--text)' }}>{l.title}</p>
                          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{l.category_name}</p>
                        </div>
                        {l.featured && <Star size={13} fill="var(--gold)" style={{ color: 'var(--gold)', flexShrink: 0 }} />}
                      </div>
                    );
                  },
                },
                { key: 'price', label: 'Price', render: (l) => <span style={{ fontFamily: 'var(--font-serif)', color: 'var(--gold)', fontStyle: 'italic' }}>{formatPrice(l.price)}</span> },
                { key: 'created', label: 'Created', render: (l) => <span style={{ color: 'var(--text-muted)' }}>{formatDate(l.created_at)}</span> },
                { key: 'status', label: 'Status', render: (l) => {
                  const b = LISTING_BADGE[l.status] || LISTING_BADGE.draft;
                  return <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium capitalize" style={b.style}>{l.status}</span>;
                } },
                {
                  key: 'actions', label: '', width: '60px',
                  render: (l) => (
                    <a href={`/listings/${l.id}`} target="_blank" rel="noreferrer" className="inline-flex items-center justify-center h-8 w-8 rounded-lg transition-colors hover:bg-[var(--bg-subtle)]" style={{ color: 'var(--text-muted)' }} title="View listing">
                      <ExternalLink size={15} />
                    </a>
                  ),
                },
              ]}
            />
          </TabsContent>

          {/* Conversations */}
          <TabsContent value="conversations" className="mt-5">
            <AdminTable
              data={data.conversations || []}
              dataTestId="member-conversations-table"
              emptyTitle="No conversations"
              emptySubtitle="This member hasn’t started or received any enquiries yet."
              columns={[
                {
                  key: 'parties', label: 'Conversation',
                  render: (c) => {
                    const thumb = c.listing_image ? getImageUrl(c.listing_image) : PLACEHOLDER_IMAGE;
                    return (
                      <div className="flex items-center gap-3">
                        <img src={thumb} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" style={{ backgroundColor: 'var(--bg-subtle)' }} />
                        <div className="min-w-0">
                          <p className="font-medium truncate" style={{ color: 'var(--text)' }}>{c.buyer_name} ↔ {c.seller_name}</p>
                          <p className="text-xs italic truncate" style={{ color: 'var(--text-muted)' }}>{c.listing_title}</p>
                        </div>
                      </div>
                    );
                  },
                },
                { key: 'role', label: 'Role', render: (c) => <span className="text-xs capitalize" style={{ color: 'var(--text-muted)' }}>{c.role_in_conv || '—'}</span> },
                { key: 'last', label: 'Last message', render: (c) => <p className="text-xs truncate max-w-xs" style={{ color: 'var(--text-muted)' }}>{c.last_message || '…'}</p> },
                { key: 'when', label: 'Updated', render: (c) => <span style={{ color: 'var(--text-muted)' }}>{formatDate(c.last_message_at)}</span> },
                {
                  key: 'actions', label: '', width: '60px',
                  render: () => (
                    <Link to="/admin/conversations" className="inline-flex items-center justify-center h-8 w-8 rounded-lg transition-colors hover:bg-[var(--bg-subtle)]" style={{ color: 'var(--text-muted)' }} title="Open in monitor">
                      <ExternalLink size={15} />
                    </Link>
                  ),
                },
              ]}
            />
          </TabsContent>

          {/* Transactions (placeholder) */}
          <TabsContent value="transactions" className="mt-5">
            <div className="rounded-2xl py-16 text-center" style={{ backgroundColor: 'var(--surface)', border: '1px solid var(--border-clr)' }} data-testid="member-transactions-empty">
              <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-3" style={{ backgroundColor: 'var(--bg-subtle)' }}>
                <CreditCard size={20} style={{ color: 'var(--text-muted)' }} />
              </div>
              <p className="text-sm font-medium" style={{ color: 'var(--text)' }}>No transactions yet</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>Payment history will appear here once payments go live.</p>
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Change role dialog */}
      <Dialog open={roleDialog} onOpenChange={setRoleDialog}>
        <DialogContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>Change role — {user.name}</DialogTitle>
          </DialogHeader>
          <div className="py-2">
            <label className="block text-xs font-medium mb-1.5" style={{ color: 'var(--text-muted)' }}>New role</label>
            <Select value={newRole} onValueChange={setNewRole}>
              <SelectTrigger style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }}><SelectValue /></SelectTrigger>
              <SelectContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
                <SelectItem value="applicant">Applicant</SelectItem>
                <SelectItem value="member">Member</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setRoleDialog(false)} disabled={submitting} style={{ borderRadius: '10px' }}>Cancel</Button>
            <Button onClick={submitRole} disabled={submitting || newRole === user.role} style={{ backgroundColor: 'var(--text)', color: 'var(--bg)', borderRadius: '10px' }} data-testid="member-role-save">
              {submitting ? 'Saving…' : 'Save role'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review dialog */}
      <Dialog open={!!reviewDialog} onOpenChange={() => setReviewDialog(null)}>
        <DialogContent style={{ backgroundColor: 'var(--surface)', borderColor: 'var(--border-clr)' }}>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'var(--font-serif)', color: 'var(--text)' }}>
              {reviewDialog === 'approved' ? 'Approve' : 'Reject'} application
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
              {reviewDialog === 'approved'
                ? `${user.name} will be notified and granted member access.`
                : `${user.name} will be notified that their application was not successful.`}
            </p>
            <Textarea value={notes} onChange={e => setNotes(e.target.value)} placeholder="Admin notes…" rows={3} className="text-sm" style={{ borderColor: 'var(--border-clr)', backgroundColor: 'var(--surface)' }} />
          </div>
          <DialogFooter className="gap-2">
            <Button variant="ghost" onClick={() => setReviewDialog(null)} disabled={submitting} style={{ borderRadius: '10px' }}>Cancel</Button>
            <Button onClick={submitReview} disabled={submitting || (reviewDialog === 'rejected' && !notes.trim())}
              style={{ backgroundColor: reviewDialog === 'approved' ? 'var(--success)' : 'var(--danger)', color: 'white', borderRadius: '10px' }} data-testid="member-review-confirm">
              {submitting ? 'Processing…' : reviewDialog === 'approved' ? 'Approve' : 'Reject'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardShell>
  );
}
